import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import api from '../../api/client';
import { toast } from '../../api/toast';

/* حساب‌هایی که در مسیرِ «اولین ورود» نیاز به بررسی دارند + اقدام‌های ادمین. */

const REASONS = {
  no_phone: 'بدون شماره تلفن',
  no_contract_data: 'بدون دادهٔ قرارداد',
  phone_mismatch: 'شماره متفاوت با فایل',
  claimed_unverified: 'ادعا بدون تأیید شمارهٔ فایل',
  otp_failed: 'خطای مکرر کد',
  dup_phone: 'شمارهٔ تکراری روی چند حساب',
  legacy_password: 'رمز قدیمی (کد ملی)',
  disputed: 'ادعای مورد اعتراض',
  admin_reset: 'ریست توسط ادمین',
};

const ROLES = {
  Contractor: 'پیمانکار',
  Department: 'سازمان',
  Consultant: 'مشاور',
  Supervisor: 'ناظر',
  Admin: 'ادمین',
  DepartmentManager: 'مدیر سازمان',
};

const useStyles = makeStyles(() => ({
  head: { background: '#0f6e7d', color: '#fff' },
  row: { '&:nth-of-type(odd)': { background: '#f7fbfc' } },
  chip: {
    display: 'inline-block', padding: '2px 10px', borderRadius: 999,
    background: '#fef3c7', color: '#92400e', fontWeight: 800, fontSize: 12,
  },
  muted: { color: '#64748b', fontSize: 12 },
}));

export default function AuthReview() {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // {type, id, title}
  const [mobile, setMobile] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('users/admin/auth-review/');
      setRows((res.data && res.data.users) || []);
    } catch (e) {
      toast('خواندنِ فهرست ممکن نشد', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async () => {
    if (!dialog) return;
    try {
      if (dialog.type === 'resend') {
        await api.post(`users/admin/users/${dialog.id}/resend-otp/`);
        toast('کد به شمارهٔ ثبت‌شده ارسال شد');
      } else if (dialog.type === 'phone') {
        await api.post(`users/admin/users/${dialog.id}/reset-phone/`, { mobile, reason });
        toast('شماره به‌روزرسانی شد');
      } else if (dialog.type === 'unclaim') {
        await api.post(`users/admin/users/${dialog.id}/unclaim/`, { reason });
        toast('ادعا باطل شد');
      }
      setDialog(null);
      setMobile('');
      setReason('');
      load();
    } catch (e) {
      const msg = (e.response && e.response.data && (e.response.data.detail || e.response.data.error))
        || 'عملیات انجام نشد';
      toast(msg, 'error');
    }
  };

  const navigate = useNavigate()

  return (
    <Layout title="حساب‌های نیازمند بررسی">
      <Box p={2}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          style={{ marginBottom: 12, fontWeight: 700, textTransform: 'none' }}
        >
          بازگشت به داشبورد
        </Button>
    <Card elevation={2}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Typography variant="h6" style={{ fontWeight: 800, color: '#0f172a' }}>
            حساب‌های نیازمند بررسی ({rows.length})
          </Typography>
          <Tooltip title="به‌روزرسانی">
            <span>
              <IconButton onClick={load} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.head}>کاربر</TableCell>
              <TableCell className={classes.head}>نقش</TableCell>
              <TableCell className={classes.head}>دلیل</TableCell>
              <TableCell className={classes.head}>شمارهٔ جاری</TableCell>
              <TableCell className={classes.head}>شمارهٔ اولین ورود</TableCell>
              <TableCell className={classes.head}>شمارهٔ فایل</TableCell>
              <TableCell className={classes.head}>آخرین ورود</TableCell>
              <TableCell className={classes.head}>اقدام</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} className={classes.row}>
                <TableCell>{u.username}</TableCell>
                <TableCell>{ROLES[u.role] || u.role}</TableCell>
                <TableCell><span className={classes.chip}>{REASONS[u.review_reason] || u.review_reason}</span></TableCell>
                <TableCell>{u.mobile || '—'}</TableCell>
                <TableCell>{u.first_login_mobile || '—'}</TableCell>
                <TableCell>{u.file_mobile || '—'}</TableCell>
                <TableCell className={classes.muted}>{u.last_login ? String(u.last_login).slice(0, 10) : '—'}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setDialog({ type: 'resend', id: u.id })}>ارسال کد</Button>
                  <Button size="small" onClick={() => setDialog({ type: 'phone', id: u.id })}>اصلاح شماره</Button>
                  <Button size="small" color="secondary" onClick={() => setDialog({ type: 'unclaim', id: u.id })}>
                    ابطال ادعا
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={8} className={classes.muted} style={{ textAlign: 'center', padding: 24 }}>
                  {loading ? 'در حال خواندن…' : 'موردی برای بررسی نیست.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {dialog?.type === 'resend' && 'ارسال دوبارهٔ کد'}
          {dialog?.type === 'phone' && 'اصلاحِ شمارهٔ تلفن'}
          {dialog?.type === 'unclaim' && 'ابطالِ ادعا'}
        </DialogTitle>
        <DialogContent>
          {dialog?.type === 'phone' && (
            <TextField
              fullWidth margin="normal" label="شمارهٔ جدید" value={mobile}
              onChange={(e) => setMobile(e.target.value)} placeholder="09123456789"
            />
          )}
          {dialog?.type !== 'resend' && (
            <TextField
              fullWidth margin="normal" label="دلیل (اجباری)" value={reason}
              onChange={(e) => setReason(e.target.value)} multiline rows={2}
            />
          )}
          {dialog?.type === 'resend' && (
            <Typography variant="body2" style={{ color: '#475569', lineHeight: 2 }}>
              کد به شمارهٔ ثبت‌شدهٔ همین حساب ارسال می‌شود؛ شمارهٔ دیگری قابلِ انتخاب نیست.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>انصراف</Button>
          <Button color="primary" variant="contained" onClick={act}>تأیید</Button>
        </DialogActions>
      </Dialog>
    </Card>
      </Box>
    </Layout>
  );
}
